import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage, auth } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import FormatBar from '../format_bar/formatBar';
import './adminSupervision.css';

const AdminSupervision = () => {
  const navigate = useNavigate();
  const introTextRef = useRef(null);
  const bottomTextRef = useRef(null);
  const eventDescriptionRef = useRef(null);

  // Auth state
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Data states
  const [supervisionData, setSupervisionData] = useState({
    title: '',
    heroImage: '',
    introText: '',
    bottomText: ''
  });
  
  const [supervisionEvents, setSupervisionEvents] = useState([]);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [heroFile, setHeroFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [heroPreview, setHeroPreview] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  
  // UI states
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Format states
  const [introTextFormats, setIntroTextFormats] = useState({
    bold: false,
    italic: false,
    heading: false,
    bullet: false,
    link: false,
    underline: false
  });

  const [bottomTextFormats, setBottomTextFormats] = useState({
    bold: false,
    italic: false,
    heading: false,
    bullet: false,
    link: false,
    underline: false
  });

  const [eventDescriptionFormats, setEventDescriptionFormats] = useState({
    bold: false,
    italic: false,
    heading: false,
    bullet: false,
    link: false,
    underline: false
  });

  // Check auth
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setIsLoading(false);
      if (!user) {
        navigate('/admin/login');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Load content
  useEffect(() => {
    const fetchSupervisionData = async () => {
      try {
        // Fetch supervision main content
        const supervisionDocRef = doc(db, 'content', 'supervision');
        const supervisionDoc = await getDoc(supervisionDocRef);
        
        if (supervisionDoc.exists()) {
          const data = supervisionDoc.data();
          setSupervisionData({
            title: data.title || '',
            heroImage: data.heroImage || '',
            introText: data.introText || '',
            bottomText: data.bottomText || ''
          });
          
          if (data.heroImage) {
            setHeroPreview(data.heroImage);
          }
        }
        
        // Fetch supervision events
        const supervisionEventsDocRef = doc(db, 'content', 'supervisionEvents');
        const supervisionEventsDoc = await getDoc(supervisionEventsDocRef);
        
        if (supervisionEventsDoc.exists() && supervisionEventsDoc.data().events) {
          setSupervisionEvents(supervisionEventsDoc.data().events);
        } else {
          // Fallback til standard events hvis data ikke findes
          setSupervisionEvents([]);
        }
      } catch (error) {
        console.error('Fejl ved hentning af supervision data:', error);
        setMessage({
          text: 'Der opstod en fejl ved indlæsning af indhold',
          type: 'error'
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      fetchSupervisionData();
    }
  }, [user]);

  // Initialisering af editorer
  useEffect(() => {
    if (isEditing) {
      if (introTextRef.current) {
        introTextRef.current.innerHTML = supervisionData.introText || '';
      }
      if (bottomTextRef.current) {
        bottomTextRef.current.innerHTML = supervisionData.bottomText || '';
      }
    }

    if (editingEvent && eventDescriptionRef.current) {
      eventDescriptionRef.current.innerHTML = editingEvent.description || '';
    }
  }, [isEditing, editingEvent, supervisionData.introText, supervisionData.bottomText]);

  // Handlere for hovedindhold
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSupervisionData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleHeroFileChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      setHeroFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setHeroPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handlere for supervision events
  const handleEditEvent = (event) => {
    setEditingEvent({...event});
    setPreview(event.imageUrl);
    setIsCreating(false);
  };

  const handleCreateEvent = () => {
    setEditingEvent({
      id: '',
      title: '',
      description: '',
      imageUrl: '',
      externalLink: '',
      buttonText: 'Læs mere'
    });
    setPreview(null);
    setSelectedFile(null);
    setIsCreating(true);
  };

  const handleEventInputChange = (e) => {
    const { name, value } = e.target;
    setEditingEvent(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (window.confirm('Er du sikker på, at du vil slette dette event?')) {
      try {
        const event = supervisionEvents.find(e => e.id === eventId);
        
        // Slet billedet hvis det er gemt i storage
        if (event.imageUrl && event.imageUrl.includes('firebase')) {
          const imagePath = event.imageUrl.split('/').pop();
          const imageRef = ref(storage, `supervision-events/${imagePath}`);
          await deleteObject(imageRef);
        }
        
        // Opdater array uden det slettede event
        const updatedEvents = supervisionEvents.filter(e => e.id !== eventId);
        setSupervisionEvents(updatedEvents);
        
        // Gem opdateret array i Firestore
        await setDoc(doc(db, 'content', 'supervisionEvents'), {
          events: updatedEvents,
          lastUpdated: new Date()
        });
        
        setMessage({ type: 'success', text: 'Event blev slettet!' });
      } catch (error) {
        console.error("Fejl ved sletning:", error);
        setMessage({ type: 'error', text: 'Fejl ved sletning af event.' });
      }
    }
  };

  const handleSaveEvent = async () => {
    setIsSaving(true);
    try {
      if (!editingEvent.id && isCreating) {
        // Generer et ID baseret på titlen hvis det er et nyt event
        editingEvent.id = editingEvent.title
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9]/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-|-$/g, '');
      }
      
      let imageUrl = editingEvent.imageUrl;
      
      // Upload nyt billede hvis det er valgt
      if (selectedFile) {
        const storageRef = ref(storage, `supervision-events/${editingEvent.id}-${Date.now()}`);
        await uploadBytes(storageRef, selectedFile);
        imageUrl = await getDownloadURL(storageRef);
      }
      
      // Hent det formaterede indhold fra editoren direkte
      const description = eventDescriptionRef.current ? eventDescriptionRef.current.innerHTML : '';
      
      const updatedEvent = {
        ...editingEvent,
        imageUrl,
        description
      };
      
      let updatedEvents;
      
      if (isCreating) {
        updatedEvents = [...supervisionEvents, updatedEvent];
      } else {
        updatedEvents = supervisionEvents.map(e => 
          e.id === updatedEvent.id ? updatedEvent : e
        );
      }
      
      // Gem opdateret array i Firestore
      await setDoc(doc(db, 'content', 'supervisionEvents'), {
        events: updatedEvents,
        lastUpdated: new Date()
      });
      
      setSupervisionEvents(updatedEvents);
      setEditingEvent(null);
      setSelectedFile(null);
      setPreview(null);
      setIsCreating(false);
      
      setMessage({ 
        type: 'success', 
        text: isCreating ? 'Nyt event oprettet!' : 'Event opdateret!' 
      });
    } catch (error) {
      console.error("Fejl ved gem:", error);
      setMessage({ 
        type: 'error', 
        text: 'Fejl ved gem af event.' 
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveHovedindhold = async () => {
    setIsSaving(true);
    try {
      let heroImageUrl = supervisionData.heroImage;
      
      // Upload nyt hero billede hvis det er valgt
      if (heroFile) {
        const storageRef = ref(storage, `supervision/hero-${Date.now()}`);
        await uploadBytes(storageRef, heroFile);
        heroImageUrl = await getDownloadURL(storageRef);
      }
      
      // Hent det formaterede indhold fra editorerne direkte
      const introText = introTextRef.current ? introTextRef.current.innerHTML : '';
      const bottomText = bottomTextRef.current ? bottomTextRef.current.innerHTML : '';
      
      // Gem hovedindhold i Firestore
      await setDoc(doc(db, 'content', 'supervision'), {
        ...supervisionData,
        introText,
        bottomText,
        heroImage: heroImageUrl,
        lastUpdated: new Date()
      });
      
      setSupervisionData(prev => ({
        ...prev,
        introText,
        bottomText,
        heroImage: heroImageUrl
      }));
      
      setIsEditing(false);
      setHeroFile(null);
      setMessage({ type: 'success', text: 'Supervisionssiden blev opdateret!' });
    } catch (error) {
      console.error("Fejl ved gem:", error);
      setMessage({ type: 'error', text: 'Fejl ved gem af supervisionsside.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingEvent(null);
    setPreview(null);
    setSelectedFile(null);
    setIsCreating(false);
    setIsEditing(false);
    setHeroFile(null);
  };

  const closeMessage = () => {
    setMessage({ type: '', text: '' });
  };

  const goBack = () => {
    navigate('/admin');
  };

  // Tilføj event listener for selection change
  useEffect(() => {
    const handleSelectionChange = () => {
      // Kun tjek formateringer hvis vi er i redigeringstilstand
      if (!isEditing && !editingEvent) return;
      
      // Find ud af hvilken editor der har fokus
      const activeElement = document.activeElement;
      const isIntroText = introTextRef.current && introTextRef.current.contains(activeElement);
      const isBottomText = bottomTextRef.current && bottomTextRef.current.contains(activeElement);
      const isEventDescription = eventDescriptionRef.current && eventDescriptionRef.current.contains(activeElement);
      
      if (isIntroText) {
        checkActiveFormats('introText');
      } else if (isBottomText) {
        checkActiveFormats('bottomText');
      } else if (isEventDescription) {
        checkActiveFormats('eventDescription');
      }
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [isEditing, editingEvent]);

  const handleEditorFocus = (editorType) => {
    const editor = {
      introText: introTextRef.current,
      bottomText: bottomTextRef.current,
      eventDescription: eventDescriptionRef.current
    }[editorType];

    if (editor) {
      editor.focus();
      checkActiveFormats(editorType);
    }
  };

  const checkActiveFormats = (editorType) => {
    const setFormats = {
      introText: setIntroTextFormats,
      bottomText: setBottomTextFormats,
      eventDescription: setEventDescriptionFormats
    }[editorType];
    
    setFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      heading: false,
      bullet: document.queryCommandState('insertUnorderedList'),
      link: document.queryCommandState('createLink'),
      underline: document.queryCommandState('underline')
    });
  };

  // Håndter link-indsættelse
  const handleLinkInsertion = (editor) => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    
    const range = selection.getRangeAt(0);
    const selectedText = range.toString().trim();
    
    // Tjek om selektionen er inden for editoren
    if (!editor || !editor.contains(range.commonAncestorContainer)) {
      return;
    }
    
    // Tjek om den valgte tekst er en URL
    const url = isValidUrl(selectedText) ? selectedText : prompt('Indtast URL:', 'https://');
    
    if (url && url.trim() !== '') {
      const linkText = selectedText || prompt('Indtast link tekst:', '');
      
      if (linkText && linkText.trim() !== '') {
        // Indsæt link direkte i HTML
        const linkElement = document.createElement('a');
        linkElement.href = url;
        linkElement.target = '_blank';
        linkElement.rel = 'noopener noreferrer';
        linkElement.textContent = linkText;
        
        // Indsæt linket
        range.deleteContents();
        range.insertNode(linkElement);
        
        // Flyt cursoren efter linket
        range.setStartAfter(linkElement);
        range.setEndAfter(linkElement);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  };

  const handleFormatClick = (formatType, editorType) => {
    const editor = {
      introText: introTextRef.current,
      bottomText: bottomTextRef.current,
      eventDescription: eventDescriptionRef.current
    }[editorType];

    if (!editor) return;
    
    // Fokuser på editoren hvis den ikke allerede har fokus
    if (!editor.contains(document.activeElement)) {
      editor.focus();
    }

    // Udfør formateringen direkte
    switch (formatType) {
      case 'bold':
        document.execCommand('bold', false);
        break;
      
      case 'italic':
        document.execCommand('italic', false);
        break;
      
      case 'heading':
        const selection = window.getSelection();
        if (!selection.rangeCount) return;
        
        const range = selection.getRangeAt(0);
        if (!range) return;
        
        const parentElement = range.commonAncestorContainer.parentElement;
        const isHeading = parentElement && parentElement.tagName === 'H2';
        
        if (isHeading) {
          document.execCommand('formatBlock', false, 'p');
        } else {
          document.execCommand('formatBlock', false, 'h2');
        }
        break;
      
      case 'bullet':
        document.execCommand('insertUnorderedList', false);
        break;
      
      case 'link':
        handleLinkInsertion(editor);
        break;
      
      case 'underline':
        document.execCommand('underline', false);
        break;
      
      default:
        break;
    }

    // Opdater aktive formateringer
    checkActiveFormats(editorType);
  };

  const isValidUrl = (text) => {
    try {
      if (text.match(/^(https?:\/\/|www\.)/i)) {
        const url = text.startsWith('www.') ? `https://${text}` : text;
        new URL(url);
        return url;
      }
      return false;
    } catch {
      return false;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      document.execCommand('insertLineBreak', false);
    }
  };

  if (isLoading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Indlæser...</p>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <header className="admin-header">
        <div className="admin-logo">
          <img 
            src="/media/logo/psykologiskperspektiv-logo.avif" 
            alt="Psykologisk Perspektiv" 
            onClick={() => navigate('/admin')}
            style={{ cursor: 'pointer' }}
          />
        </div>
        <div className="admin-header-right">
          <div className="admin-user">
            <span>Logget ind som: </span>
            <strong>{user.email}</strong>
          </div>
          <button onClick={() => navigate('/admin')} className="back-button">
            ← Tilbage
          </button>
        </div>
      </header>
      
      <main className="admin-content">
        <div className="admin-supervision-container">
          <button className="back-button" onClick={goBack}>
            ← Tilbage til Admin
          </button>
          
          <h1>Administrer Supervision</h1>
          
          {message.text && (
            <div className={`admin-message ${message.type}`}>
              <p>{message.text}</p>
              <button 
                className="close-message" 
                onClick={closeMessage}
              >
                ×
              </button>
            </div>
          )}
          
          <div className="admin-section">
            <div className="admin-section-header">
              <h2>Hovedindhold på Supervisionssiden</h2>
              <button 
                className="edit-content-btn"
                onClick={() => setIsEditing(true)}
                disabled={isEditing}
              >
                Rediger indhold
              </button>
            </div>
            
            {isEditing ? (
              <div className="supervision-form">
                <div className="form-group">
                  <label htmlFor="title">Overskrift</label>
                  <input 
                    type="text" 
                    id="title" 
                    name="title"
                    value={supervisionData.title || ''}
                    onChange={handleInputChange}
                  />
                </div>
                
                <div className="image-uploader">
                  <label>Hovedbillede</label>
                  
                  {heroPreview && (
                    <div className="image-preview hero-preview">
                      <img src={heroPreview} alt="Forhåndsvisning" />
                    </div>
                  )}
                  
                  <label className="upload-btn">
                    {heroPreview ? 'Skift billede' : 'Vælg billede'}
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleHeroFileChange}
                      id="hero-image-upload"
                    />
                  </label>
                </div>
                
                <div className="form-group">
                  <label htmlFor="introText">Tekst før events</label>
                  <FormatBar 
                    activeFormats={introTextFormats}
                    onFormatClick={(format) => handleFormatClick(format, 'introText')}
                  />
                  <div
                    ref={introTextRef}
                    className="rich-text-editor"
                    contentEditable
                    onKeyDown={handleKeyDown}
                    onFocus={() => handleEditorFocus('introText')}
                    data-placeholder="Skriv teksten der skal vises før events..."
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="bottomText">Tekst efter events</label>
                  <FormatBar 
                    activeFormats={bottomTextFormats}
                    onFormatClick={(format) => handleFormatClick(format, 'bottomText')}
                  />
                  <div
                    ref={bottomTextRef}
                    className="rich-text-editor"
                    contentEditable
                    onKeyDown={handleKeyDown}
                    onFocus={() => handleEditorFocus('bottomText')}
                    data-placeholder="Skriv teksten der skal vises efter events..."
                  />
                </div>
                
                <div className="form-actions">
                  <button 
                    className="save-btn"
                    onClick={handleSaveHovedindhold}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Gemmer...' : 'Gem ændringer'}
                  </button>
                  <button 
                    className="cancel-btn"
                    onClick={handleCancel}
                    disabled={isSaving}
                  >
                    Annuller
                  </button>
                </div>
              </div>
            ) : (
              <div className="content-preview">
                <p className="admin-description">
                  Her kan du redigere hovedindholdet på supervisionssiden, inklusiv overskrift, billede og tekster.
                </p>
                <p>Klik på "Rediger indhold" for at ændre hovedindholdet.</p>
              </div>
            )}
          </div>
          
          <div className="admin-section">
            <div className="admin-section-header">
              <h2>Events</h2>
              {!editingEvent && (
                <button 
                  className="create-btn"
                  onClick={handleCreateEvent}
                >
                  + Opret nyt event
                </button>
              )}
            </div>
            
            <p className="admin-description">
              Her kan du administrere de forskellige events der vises på supervisionssiden.
            </p>
            
            {editingEvent ? (
              <div className="supervision-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="event-title">Titel</label>
                    <input 
                      type="text" 
                      id="event-title" 
                      name="title"
                      value={editingEvent.title || ''}
                      onChange={handleEventInputChange}
                      required
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="event-description">Beskrivelse</label>
                  <FormatBar 
                    activeFormats={eventDescriptionFormats}
                    onFormatClick={(format) => handleFormatClick(format, 'eventDescription')}
                  />
                  <div
                    ref={eventDescriptionRef}
                    className="rich-text-editor"
                    contentEditable
                    onKeyDown={handleKeyDown}
                    onFocus={() => handleEditorFocus('eventDescription')}
                    data-placeholder="Skriv beskrivelse af event..."
                  />
                </div>
                
                <div className="image-uploader">
                  <label>Billede</label>
                  
                  {preview && (
                    <div className="image-preview">
                      <img src={preview} alt="Forhåndsvisning" />
                    </div>
                  )}
                  
                  <label className="upload-btn">
                    {preview ? 'Skift billede' : 'Vælg billede'}
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleFileChange}
                      id="image-upload"
                    />
                  </label>
                  
                  {!isCreating && !selectedFile && (
                    <span className="form-help-text">
                      Lad være tom for at beholde eksisterende billede
                    </span>
                  )}
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="event-externalLink">Eksternt Link</label>
                    <input 
                      type="text" 
                      id="event-externalLink" 
                      name="externalLink"
                      value={editingEvent.externalLink || ''}
                      onChange={handleEventInputChange}
                    />
                    <span className="form-help-text">
                      Fuldt URL (fx. https://example.com/event) som knappen skal linke til
                    </span>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="event-buttonText">Knaptekst</label>
                    <input 
                      type="text" 
                      id="event-buttonText" 
                      name="buttonText"
                      value={editingEvent.buttonText || 'Læs mere'}
                      onChange={handleEventInputChange}
                    />
                  </div>
                </div>
                
                <div className="form-actions">
                  <button 
                    className="save-btn"
                    onClick={handleSaveEvent}
                    disabled={isSaving || !editingEvent.title || (isCreating && !preview)}
                  >
                    {isSaving ? 'Gemmer...' : isCreating ? 'Opret event' : 'Gem ændringer'}
                  </button>
                  <button 
                    className="cancel-btn"
                    onClick={handleCancel}
                    disabled={isSaving}
                  >
                    Annuller
                  </button>
                </div>
              </div>
            ) : (
              <>
                {supervisionEvents.length > 0 ? (
                  <div className="event-list">
                    {supervisionEvents.map((event) => (
                      <div key={event.id} className="event-item">
                        <div className="event-preview">
                          <div className="event-image">
                            <img src={event.imageUrl} alt={event.title} />
                          </div>
                          <div className="event-content">
                            <h3>{event.title}</h3>
                            <p>{event.description}</p>
                            {event.externalLink && (
                              <a className="event-link" href={event.externalLink} target="_blank" rel="noopener noreferrer">
                                {event.buttonText || 'Læs mere'}
                              </a>
                            )}
                          </div>
                        </div>
                        <div className="event-actions">
                          <button 
                            className="edit-btn"
                            onClick={() => handleEditEvent(event)}
                          >
                            Rediger
                          </button>
                          <button 
                            className="delete-btn"
                            onClick={() => handleDeleteEvent(event.id)}
                          >
                            Slet
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-items">
                    <p>Ingen events fundet. Opret et nyt event for at komme i gang.</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>
      
      <footer className="admin-footer">
        <p>&copy; {new Date().getFullYear()} Psykologisk Perspektiv - Admin Panel</p>
      </footer>
    </div>
  );
};

export default AdminSupervision;
